import React, { useEffect, useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { formatDate } from '@/lib/dateUtils';

interface Recovery {
  id: string;
  txn_no: string;
  recovery_date: string;
  organization_name: string;
  customer_name: string;
  shift: string;
  deposit: number;
  received_amount: number;
  discount: number;
  pending_amount?: number;
  balance_amount?: number;
  employee_id: string;
  employee_name: string;
  notes: string;
  payment_mode?: string;
  paymentMode?: string;
  mode_type?: string;
  modeType?: string;
  bank_name_snapshot?: string;
  image?: string;
  created_at: string;
  user_log_details?: string;
  credit_customer_id?: string;
}

interface CreditCustomer {
  id: string;
  organization_name?: string;
  organizationName?: string;
  current_balance?: number;
  representative_name?: string;
  representativeName?: string;
  customer_name?: string;
}

interface Employee {
  id: string;
  employee_name: string;
  employeeName?: string;
  name?: string;
  _id?: string;
}

export interface RecoveryFormRow {
  rowId: string;
  credit_customer_id: string;
  organization_name: string;
  customer_name: string;
  pending_amount: string;
  received_amount: string;
  discount: string;
  balance_amount: string;
  payment_mode: string;
  mode_type: string;
  employee_id: string;
  employee_name: string;
  notes: string;
  image: string;
}

const createEmptyFormRow = (): RecoveryFormRow => ({
  rowId: Date.now().toString() + Math.random().toString(36).substring(2, 6),
  credit_customer_id: '',
  organization_name: '',
  customer_name: '',
  pending_amount: '',
  received_amount: '',
  discount: '0',
  balance_amount: '0',
  payment_mode: 'Cash',
  mode_type: '',
  employee_id: '',
  employee_name: '',
  notes: '',
  image: '',
});

export default function RecoverySimple() {
  const { toast } = useToast();
  const [rows, setRows] = useState<Recovery[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const [filterForm, setFilterForm] = useState({ from: '', to: '', organization: '' });
  const [showCount, setShowCount] = useState('All');
  const [creditCustomers, setCreditCustomers] = useState<CreditCustomer[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [bankParties, setBankParties] = useState<any[]>([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [loadingEmployees, setLoadingEmployees] = useState(false);

  // AutoFill selectors
  const [autoFillOrg, setAutoFillOrg] = useState('');
  const [autoFillEmp, setAutoFillEmp] = useState('');

  // Global Date & Shift
  const [globalDate, setGlobalDate] = useState(new Date().toISOString().slice(0, 10));
  const [globalShift, setGlobalShift] = useState('S-1');

  // Multi-row recovery items
  const [formItems, setFormItems] = useState<RecoveryFormRow[]>([createEmptyFormRow()]);

  useEffect(() => {
    fetchList();
    fetchCreditCustomers();
    fetchEmployees();
    fetchBankParties();
  }, []);

  const fetchBankParties = async () => {
    try {
      const res = await fetch('/api/business-parties', { credentials: 'include' });
      const data = await res.json();
      if (data.ok) {
        const banks = (data.rows || []).filter((p: any) => p.party_type === 'Bank' && p.is_active !== false);
        setBankParties(banks);
      }
    } catch (err) {
      console.error('Failed to load bank parties:', err);
    }
  };

  const fetchList = async () => {
    try {
      setLoading(true);
      const res = await fetch("/api/recoveries");
      const data = await res.json();
      if (data.ok) {
        const mapped = data.rows.map((r: any) => ({
          ...r,
          id: r._id || r.id,
        }));
        setRows(mapped);
      }
    } catch (err) {
      console.error("Fetch error:", err);
      toast({ title: "Error", description: "Failed to load data", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const fetchCreditCustomers = async () => {
    try {
      setLoadingCustomers(true);
      const response = await fetch('/api/credit-customers', { credentials: 'include' });
      const result = await response.json();
      if (result.ok) setCreditCustomers(result.rows || []);
    } catch (err) {
      console.error('Failed to load credit customers:', err);
    } finally {
      setLoadingCustomers(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      setLoadingEmployees(true);
      const response = await fetch('/api/employees', { credentials: 'include' });
      const result = await response.json();
      if (result.ok) setEmployees(result.rows || []);
    } catch (err) {
      console.error('Failed to load employees:', err);
    } finally {
      setLoadingEmployees(false);
    }
  };

  // Auto Total Recovery Cash calculation
  const totalRecoveryCash = formItems.reduce((sum, item) => {
    if (item.payment_mode === 'Cash') {
      return sum + (Number(item.received_amount) || 0);
    }
    return sum;
  }, 0);

  // Row operations
  const handleAddRow = () => {
    setFormItems(prev => [...prev, createEmptyFormRow()]);
  };

  const handleRemoveRow = (rowId: string) => {
    if (formItems.length > 1) {
      setFormItems(prev => prev.filter(r => r.rowId !== rowId));
    }
  };

  const updateRowItem = (rowId: string, updates: Partial<RecoveryFormRow>) => {
    setFormItems(prev => prev.map(item => {
      if (item.rowId !== rowId) return item;
      const updated = { ...item, ...updates };

      // Auto-calculate balance
      const pending = Number(updated.pending_amount) || 0;
      const received = Number(updated.received_amount) || 0;
      const discount = Number(updated.discount) || 0;
      const balance = Math.max(0, pending - received - discount);
      updated.balance_amount = String(balance);

      return updated;
    }));
  };

  // AutoFill Action
  const handleAutoFill = () => {
    if (!autoFillOrg && !autoFillEmp) {
      toast({ title: 'Select', description: 'Please select an organization or employee first', variant: 'destructive' });
      return;
    }
    const sel = creditCustomers.find((c: any) => String(c.id || c._id) === String(autoFillOrg)) as any;
    const emp = employees.find((x: any) => String(x.id || x._id) === String(autoFillEmp)) as any;

    setFormItems(prev => {
      // Find first empty row or update the active row
      const targetIdx = prev.findIndex(r => !r.credit_customer_id && !r.customer_name);
      const idxToUpdate = targetIdx >= 0 ? targetIdx : prev.length - 1;

      return prev.map((item, idx) => {
        if (idx !== idxToUpdate) return item;
        const pending = autoFillOrg ? Number(sel?.current_balance ?? 0) : Number(item.pending_amount) || 0;
        const received = Number(item.received_amount) || 0;
        const discount = Number(item.discount) || 0;
        const balance = Math.max(0, pending - received - discount);

        return {
          ...item,
          credit_customer_id: autoFillOrg || item.credit_customer_id,
          organization_name: sel?.organization_name || sel?.organizationName || item.organization_name,
          customer_name: sel?.representative_name || sel?.representativeName || sel?.customer_name || item.customer_name,
          pending_amount: autoFillOrg ? String(sel?.current_balance ?? 0) : item.pending_amount,
          balance_amount: String(balance),
          employee_id: autoFillEmp || item.employee_id,
          employee_name: emp?.employee_name || emp?.employeeName || emp?.name || item.employee_name,
        };
      });
    });
  };

  const handleSave = async () => {
    // Validate all items
    for (let i = 0; i < formItems.length; i++) {
      const item = formItems[i];
      const rowNum = i + 1;
      if (!globalDate) {
        toast({ title: 'Error', description: 'Select a recovery date', variant: 'destructive' });
        return;
      }
      if (!item.credit_customer_id && !item.customer_name.trim()) {
        toast({ title: 'Error', description: `Row ${rowNum}: Select organization or enter customer name`, variant: 'destructive' });
        return;
      }
      if (!item.employee_id && !item.employee_name.trim()) {
        toast({ title: 'Error', description: `Row ${rowNum}: Select employee`, variant: 'destructive' });
        return;
      }
    }

    try {
      setLoading(true);
      for (const item of formItems) {
        const pending = Number(item.pending_amount) || 0;
        const received = Number(item.received_amount) || 0;
        const discount = Number(item.discount) || 0;
        const balance = Math.max(0, pending - received - discount);

        const payload = {
          recovery_date: globalDate,
          shift: globalShift,
          credit_customer_id: item.credit_customer_id || null,
          organization_name: item.organization_name,
          customer_name: item.customer_name,
          pending_amount: pending,
          received_amount: received,
          discount: discount,
          balance_amount: balance,
          payment_mode: item.payment_mode,
          mode_type: item.mode_type,
          employee_id: item.employee_id || null,
          employee_name: item.employee_name,
          notes: item.notes,
          image: item.image,
        };

        const url = editingId ? `/api/recoveries/${editingId}` : "/api/recoveries";
        const method = editingId ? "PUT" : "POST";
        const res = await fetch(url, {
          method,
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(payload),
        });
        const data = await res.json();
        if (!data.ok) throw new Error(data.error || "Failed to save recovery");
      }

      toast({
        title: editingId ? "Updated" : "Saved",
        description: editingId ? "Recovery updated successfully" : `${formItems.length} Recovery transaction(s) recorded successfully`,
      });

      setEditingId(null);
      setFormItems([createEmptyFormRow()]);
      fetchList();
    } catch (err: any) {
      console.error("Save error:", err);
      toast({ title: "Error", description: err.message || "Failed to save", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const editRow = (r: Recovery) => {
    setEditingId(r.id);
    setGlobalDate(r.recovery_date ? r.recovery_date.slice(0, 10) : new Date().toISOString().slice(0, 10));
    setGlobalShift(r.shift || 'S-1');
    setFormItems([{
      rowId: Date.now().toString(),
      credit_customer_id: r.credit_customer_id || '',
      organization_name: r.organization_name || '',
      customer_name: r.customer_name || '',
      pending_amount: String(r.pending_amount ?? 0),
      received_amount: String(r.received_amount ?? 0),
      discount: String(r.discount ?? 0),
      balance_amount: String(r.balance_amount ?? 0),
      payment_mode: r.payment_mode || r.paymentMode || 'Cash',
      mode_type: r.mode_type || r.modeType || r.bank_name_snapshot || '',
      employee_id: r.employee_id || '',
      employee_name: r.employee_name || '',
      notes: r.notes || '',
      image: r.image || '',
    }]);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const delRow = async (id: string) => {
    if (!confirm('Are you sure you want to delete this recovery?')) return;
    try {
      setLoading(true);
      const res = await fetch(`/api/recoveries/${id}`, { method: 'DELETE', credentials: 'include' });
      const data = await res.json();
      if (data.ok) {
        toast({ title: "Deleted", description: "Recovery entry deleted successfully" });
        fetchList();
      } else {
        toast({ title: "Error", description: data.error || "Failed to delete", variant: "destructive" });
      }
    } catch (err) {
      console.error("Delete error:", err);
      toast({ title: "Error", description: "Failed to delete entry", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setFormItems([createEmptyFormRow()]);
  };

  // Filter & Search
  const filteredRows = rows.filter(r => {
    if (filterForm.from && r.recovery_date < filterForm.from) return false;
    if (filterForm.to && r.recovery_date > filterForm.to) return false;
    if (filterForm.organization && r.organization_name !== filterForm.organization) return false;
    if (searchText) {
      const q = searchText.toLowerCase();
      const match = (r.organization_name || '').toLowerCase().includes(q) ||
        (r.customer_name || '').toLowerCase().includes(q) ||
        (r.txn_no || '').toLowerCase().includes(q) ||
        (r.employee_name || '').toLowerCase().includes(q);
      if (!match) return false;
    }
    return true;
  }).slice(0, showCount === 'All' ? undefined : Number(showCount));

  const fmt = (v: number | string) => Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const labelStyle: React.CSSProperties = {
    display: 'block', fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.9)',
    marginBottom: 4, letterSpacing: '0.05em', textTransform: 'uppercase'
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '9px 12px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.3)',
    background: '#ffffff', color: '#1e1b4b', fontSize: 13, fontWeight: 500, outline: 'none',
    boxSizing: 'border-box'
  };

  const readonlyStyle: React.CSSProperties = {
    ...inputStyle, background: '#f1f5f9', color: '#475569', cursor: 'not-allowed'
  };

  return (
    <div style={{ padding: 24, background: '#f8fafc', minHeight: '100vh', fontFamily: 'Inter, sans-serif' }}>
      {/* Top Banner / Title */}
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1e1b4b', margin: 0 }}>Recovery</h1>
        <p style={{ fontSize: 13, color: '#64748b', margin: '2px 0 0 0' }}>Manage recovery transactions</p>
      </div>

      {/* Main Form Container */}
      <div style={{
        background: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)',
        borderRadius: 16, padding: 24, marginBottom: 24, boxShadow: '0 4px 20px rgba(30,58,138,0.25)'
      }}>
        {/* Header Title Bar */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <span style={{ color: '#fff', fontWeight: 800, fontSize: 15, letterSpacing: '0.05em' }}>
            {editingId ? '✏️ EDIT RECOVERY' : '+ NEW RECOVERY'}
          </span>
        </div>

        {/* Global Controls Row: Date | Shift | Total Recovery Cash */}
        <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1.2fr 1fr', gap: 16, marginBottom: 20, alignItems: 'center' }}>
          <div>
            <label style={labelStyle}>RECOVERED DATE *</label>
            <input
              type="date"
              value={globalDate}
              onChange={e => setGlobalDate(e.target.value)}
              style={{ ...inputStyle, fontWeight: 600 }}
            />
          </div>

          <div>
            <label style={labelStyle}>SHIFT *</label>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center', height: 38 }}>
              {['S-1', 'S-2'].map(s => (
                <label key={s} style={{ color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input
                    type="radio"
                    name="globalShift"
                    value={s}
                    checked={globalShift === s}
                    onChange={() => setGlobalShift(s)}
                    style={{ accentColor: '#fbbf24', cursor: 'pointer' }}
                  />
                  {s}
                </label>
              ))}
            </div>
          </div>

          <div>
            <label style={labelStyle}>TOTAL RECOVERY CASH (AUTO-FILL)</label>
            <div style={{ ...inputStyle, background: '#f8fafc', fontWeight: 800, color: '#16a34a', fontSize: 14, display: 'flex', alignItems: 'center' }}>
              ₹{totalRecoveryCash.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
          </div>
        </div>

        {/* AUTO-FILL Box */}
        <div style={{
          border: '1px solid rgba(255,255,255,0.25)', borderRadius: 10, padding: 14,
          marginBottom: 20, background: 'rgba(255,255,255,0.08)'
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.8)', marginBottom: 8, letterSpacing: '0.05em' }}>AUTO-FILL</div>
          <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end' }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>CHOOSE ORGANIZATION</label>
              <select
                value={autoFillOrg}
                onChange={e => {
                  setAutoFillOrg(e.target.value);
                  const sel = creditCustomers.find((c: any) => String(c.id || c._id) === String(e.target.value)) as any;
                  if (sel) {
                    const name = sel.representative_name || sel.representativeName || sel.customer_name || '';
                  }
                }}
                style={{ ...inputStyle, cursor: 'pointer' }}
              >
                <option value="">Select</option>
                {creditCustomers.map(c => (
                  <option key={c.id} value={c.id}>{c.organization_name || c.organizationName}</option>
                ))}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>CHOOSE EMPLOYEE</label>
              <select
                value={autoFillEmp}
                onChange={e => setAutoFillEmp(e.target.value)}
                style={{ ...inputStyle, cursor: 'pointer' }}
              >
                <option value="">Select</option>
                {employees.map(emp => {
                  const empId = emp.id || emp._id;
                  return <option key={empId} value={empId}>{emp.employee_name || emp.employeeName || emp.name}</option>;
                })}
              </select>
            </div>
            <button
              onClick={handleAutoFill}
              style={{
                background: '#fbbf24', color: '#1e1b4b', border: 'none', borderRadius: 8,
                padding: '9px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap'
              }}
            >
              AUTO-FILL
            </button>
          </div>
        </div>

        {/* Dynamic Multi-Row Recovery Items List */}
        {formItems.map((item, index) => {
          const isFirstRow = index === 0;
          return (
            <div
              key={item.rowId}
              style={{
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: 12,
                padding: 16,
                marginBottom: 16,
                position: 'relative'
              }}
            >
              {/* Row 1: Organization | Customer Name | Pending | Received | Discount | Balance */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 12, marginBottom: 12 }}>
                <div>
                  <label style={labelStyle}>Organization</label>
                  <input
                    type="text"
                    value={item.organization_name}
                    onChange={e => updateRowItem(item.rowId, { organization_name: e.target.value })}
                    placeholder="Organization name"
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Customer Name</label>
                  <input
                    type="text"
                    value={item.customer_name}
                    onChange={e => updateRowItem(item.rowId, { customer_name: e.target.value })}
                    placeholder="Customer name"
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Pending (Outstanding)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={item.pending_amount}
                    onChange={e => updateRowItem(item.rowId, { pending_amount: e.target.value })}
                    placeholder="0.00"
                    style={{ ...inputStyle, background: '#fffbeb', fontWeight: 600 }}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Received Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    value={item.received_amount}
                    onChange={e => updateRowItem(item.rowId, { received_amount: e.target.value })}
                    placeholder="0.00"
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Discount Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    value={item.discount}
                    onChange={e => updateRowItem(item.rowId, { discount: e.target.value })}
                    placeholder="0.00"
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Balance (Auto-fill)</label>
                  <input
                    type="number"
                    value={item.balance_amount}
                    readOnly
                    style={{ ...readonlyStyle, fontWeight: 700 }}
                  />
                </div>
              </div>

              {/* Row 2: Employee | Collection Mode | Mode Type | Description | Upload | Action Icons */}
              <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1.2fr 1.2fr 1.8fr 1.5fr auto', gap: 12, alignItems: 'flex-end' }}>
                <div>
                  <label style={labelStyle}>Employee *</label>
                  <select
                    value={item.employee_id}
                    onChange={e => {
                      const emp = employees.find((x: any) => String(x.id || x._id) === String(e.target.value)) as any;
                      updateRowItem(item.rowId, {
                        employee_id: e.target.value,
                        employee_name: emp?.employee_name || emp?.employeeName || emp?.name || '',
                      });
                    }}
                    style={{ ...inputStyle, cursor: 'pointer' }}
                  >
                    <option value="">choose Employee</option>
                    {employees.map(emp => {
                      const empId = emp.id || emp._id;
                      return (
                        <option key={empId} value={empId}>{emp.employee_name || emp.employeeName || emp.name}</option>
                      );
                    })}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Collection Mode</label>
                  <select
                    value={item.payment_mode}
                    onChange={e => {
                      const mode = e.target.value;
                      updateRowItem(item.rowId, {
                        payment_mode: mode,
                        mode_type: mode === 'Bank' ? (item.mode_type || bankParties[0]?.party_name || bankParties[0]?.partyName || '') : mode === 'Cash' ? 'Cash' : item.mode_type
                      });
                    }}
                    style={{ ...inputStyle, cursor: 'pointer' }}
                  >
                    <option value="Cash">Cash</option>
                    <option value="Bank">Bank</option>
                    <option value="Petro Card">Petro Card</option>
                    <option value="Swipe">Swipe</option>
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Mode Type</label>
                  {item.payment_mode === 'Bank' ? (
                    <select
                      value={item.mode_type}
                      onChange={e => updateRowItem(item.rowId, { mode_type: e.target.value })}
                      style={{ ...inputStyle, cursor: 'pointer', fontWeight: 600 }}
                    >
                      <option value="">Select Bank Account</option>
                      {bankParties.map((b: any) => {
                        const bName = b.party_name || b.partyName;
                        return <option key={b.id || b._id} value={bName}>{bName}</option>;
                      })}
                      {item.mode_type && !bankParties.some((b: any) => (b.party_name || b.partyName) === item.mode_type) && (
                        <option value={item.mode_type}>{item.mode_type}</option>
                      )}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={item.mode_type}
                      onChange={e => updateRowItem(item.rowId, { mode_type: e.target.value })}
                      placeholder={item.payment_mode === 'Swipe' ? 'PNB POS' : item.payment_mode === 'Petro Card' ? 'IOCL CARD' : 'Cash'}
                      disabled={item.payment_mode === 'Cash'}
                      style={inputStyle}
                    />
                  )}
                </div>
                <div>
                  <label style={labelStyle}>Description</label>
                  <input
                    type="text"
                    value={item.notes}
                    onChange={e => updateRowItem(item.rowId, { notes: e.target.value })}
                    placeholder="Description"
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Upload Image</label>
                  <input
                    type="file"
                    accept="image/jpeg,image/jpg,image/tif,image/gif,image/png"
                    onChange={e => {
                      const file = e.target.files?.[0];
                      if (file && file.size <= 2 * 1024 * 1024) {
                        updateRowItem(item.rowId, { image: file.name });
                      } else if (file) {
                        toast({ title: 'Error', description: 'File must be under 2MB', variant: 'destructive' });
                      }
                    }}
                    style={{ ...inputStyle, padding: '6px 10px' }}
                  />
                </div>

                {/* ➕ Add Row and ✖ Delete Row Icon Controls */}
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', height: 38 }}>
                  <button
                    type="button"
                    onClick={handleAddRow}
                    title="Add Row"
                    style={{
                      background: 'rgba(255, 255, 255, 0.15)',
                      color: '#ffffff',
                      border: '2px solid rgba(255, 255, 255, 0.8)',
                      borderRadius: '50%',
                      width: 32,
                      height: 32,
                      fontSize: 18,
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                  >
                    +
                  </button>

                  {!isFirstRow && (
                    <button
                      type="button"
                      onClick={() => handleRemoveRow(item.rowId)}
                      title="Remove Row"
                      style={{
                        background: '#ef4444',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '50%',
                        width: 32,
                        height: 32,
                        fontSize: 16,
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        boxShadow: '0 2px 6px rgba(239,68,68,0.4)',
                      }}
                    >
                      ✖
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: 12, marginTop: 10 }}>
          <button
            onClick={handleSave}
            disabled={loading}
            style={{
              background: loading ? 'rgba(255,255,255,0.5)' : '#22c55e',
              color: '#fff', border: 'none', borderRadius: 8, padding: '10px 32px',
              fontSize: 14, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer',
              letterSpacing: '0.05em'
            }}
          >
            {loading ? 'Saving...' : editingId ? '✓ UPDATE' : '✓ CONFIRM'}
          </button>
          {editingId && (
            <button
              onClick={resetForm}
              style={{
                background: 'rgba(255,255,255,0.15)', color: '#fff',
                border: '1px solid rgba(255,255,255,0.4)', borderRadius: 8,
                padding: '10px 24px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      {/* Filter + Table Section */}
      <div style={{ background: '#fff', borderRadius: 16, padding: 20, boxShadow: '0 1px 6px rgba(0,0,0,0.08)' }}>
        {/* Filter */}
        <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 }}>FROM DATE</label>
            <input type="date" value={filterForm.from} onChange={e => setFilterForm(p => ({ ...p, from: e.target.value }))}
              style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14 }} />
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 }}>TO DATE</label>
            <input type="date" value={filterForm.to} onChange={e => setFilterForm(p => ({ ...p, to: e.target.value }))}
              style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14 }} />
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 }}>ORGANIZATION</label>
            <select value={filterForm.organization} onChange={e => setFilterForm(p => ({ ...p, organization: e.target.value }))}
              style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, background: '#fff', minWidth: 180 }}>
              <option value="">All</option>
              {creditCustomers.map(c => {
                const name = c.organization_name || c.organizationName || '';
                return <option key={c.id} value={name}>{name}</option>;
              })}
            </select>
          </div>
          <button onClick={fetchList}
            style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            🔄 Refresh
          </button>
          <button onClick={() => setFilterForm({ from: '', to: '', organization: '' })}
            style={{ background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 8, padding: '9px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            Clear
          </button>
        </div>

        {/* Search + Show */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <input
            type="text"
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            placeholder="Type to filter..."
            style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, width: 280 }}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13, color: '#64748b', fontWeight: 500 }}>Show:</span>
            <select value={showCount} onChange={e => setShowCount(e.target.value)}
              style={{ padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, background: '#fff' }}>
              <option value="All">All</option>
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
            </select>
          </div>
        </div>

        {/* Table */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                {['S.No', 'Txn. No', 'Date', 'Organization', 'Customer', 'Shift', 'Mode', 'Mode Type', 'Pending', 'Received', 'Discount', 'Balance', 'Employee', 'Description', 'Image', 'Action', 'User Log'].map(h => (
                  <th key={h} style={{ padding: '10px 12px', textAlign: h === 'Received' || h === 'Pending' || h === 'Discount' || h === 'Balance' ? 'right' : 'left', fontWeight: 700, color: '#374151', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={17} style={{ padding: 32, textAlign: 'center', color: '#94a3b8' }}>Loading...</td></tr>
              ) : filteredRows.length === 0 ? (
                <tr><td colSpan={17} style={{ padding: 32, textAlign: 'center', color: '#94a3b8' }}>No data available in table</td></tr>
              ) : (
                filteredRows.map((r, i) => (
                  <tr key={r.id} style={{ borderBottom: '1px solid #f1f5f9', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <td style={{ padding: '10px 12px' }}>{i + 1}</td>
                    <td style={{ padding: '10px 12px', color: '#6366f1', fontWeight: 500 }}>{r.txn_no || 'N/A'}</td>
                    <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>{formatDate(r.recovery_date)}</td>
                    <td style={{ padding: '10px 12px', fontWeight: 500 }}>{r.organization_name}</td>
                    <td style={{ padding: '10px 12px' }}>{r.customer_name}</td>
                    <td style={{ padding: '10px 12px' }}>
                      <span style={{ padding: '3px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: r.shift === 'S-1' ? '#dbeafe' : '#fef9c3', color: r.shift === 'S-1' ? '#1d4ed8' : '#a16207' }}>{r.shift || 'S-1'}</span>
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <span style={{ padding: '3px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: '#f1f5f9', color: '#475569' }}>{r.payment_mode || r.paymentMode || 'Cash'}</span>
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <span style={{ padding: '3px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: '#e0e7ff', color: '#3730a3' }}>
                        {r.mode_type || r.modeType || r.bank_name_snapshot || (r.payment_mode === 'Bank' ? 'PNB CURRENT' : r.payment_mode || 'Cash')}
                      </span>
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', color: '#dc2626', fontWeight: 500 }}>{fmt(r.pending_amount || 0)}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', color: '#16a34a', fontWeight: 600 }}>{fmt(r.received_amount || 0)}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right' }}>{fmt(r.discount || 0)}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', color: '#d97706', fontWeight: 500 }}>{fmt(r.balance_amount || r.deposit || 0)}</td>
                    <td style={{ padding: '10px 12px' }}>{r.employee_name}</td>
                    <td style={{ padding: '10px 12px', color: '#64748b', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.notes || '-'}</td>
                    <td style={{ padding: '10px 12px' }}>
                      {r.image ? <span style={{ color: '#2563eb', fontSize: 11, fontWeight: 500 }}>📎 Uploaded</span> : <span style={{ color: '#cbd5e1', fontSize: 11 }}>None</span>}
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <button onClick={() => editRow(r)} style={{ background: '#eff6ff', color: '#2563eb', border: 'none', borderRadius: 4, padding: '5px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer', marginRight: 4 }}>Edit</button>
                      <button onClick={() => delRow(r.id)} style={{ background: '#fef2f2', color: '#dc2626', border: 'none', borderRadius: 4, padding: '5px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>Del</button>
                    </td>
                    <td style={{ padding: '10px 12px', fontSize: 11, color: '#94a3b8', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.user_log_details || '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div style={{ marginTop: 12, fontSize: 12, color: '#94a3b8' }}>
          Showing {filteredRows.length} of {rows.length} entries
        </div>
      </div>
    </div>
  );
}
